import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet as WalletIcon } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PinPad, rs } from "./ui";
import { Slip } from "./Slip";

export const NAVY = "#0F2C47";
export const ORANGE = "#F26B1D";

export function BalanceCard({ wallet = "main" }) {
  const { wallet: w } = useAuth();
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-slate-100 p-4" data-testid="m-balance-card">
      <span className="grid h-11 w-11 place-items-center rounded-xl bg-white text-[#0F2C47]"><WalletIcon className="h-5 w-5" /></span>
      <div><div className="text-xs text-slate-500">{wallet === "aeps" ? "AEPS Wallet Balance" : "Wallet Balance"}</div><div className="font-display text-2xl font-bold">{rs(wallet === "aeps" ? w.aeps_balance : w.balance)}</div></div>
    </div>
  );
}

export function Label({ children, right }) {
  return <div className="mb-2 mt-5 flex items-center justify-between text-sm font-bold text-[#0F2C47]">{children}{right}</div>;
}

export function Field({ prefix, right, className = "", ...p }) {
  return (
    <div className="flex h-14 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 focus-within:border-[#0F2C47]">
      {prefix && <span className="text-lg font-semibold text-slate-700">{prefix}</span>}
      <input {...p} className={`h-full min-w-0 flex-1 bg-transparent text-base outline-none ${className}`} />
      {right}
    </div>
  );
}

export function Picker({ options, ...p }) {
  return (
    <select {...p} className="h-14 w-full rounded-xl border border-slate-200 bg-white px-4 text-base outline-none">
      {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
    </select>
  );
}

export function Chips({ values, value, onPick, testid = "m-chips" }) {
  return (
    <div className="mt-3 grid grid-cols-4 gap-2" data-testid={testid}>
      {values.map((v) => (
        <button type="button" key={v} onClick={() => onPick(String(v))} className={`h-10 rounded-xl text-sm font-semibold ${Number(value) === v ? "bg-[#F26B1D] text-white" : "bg-slate-100 text-slate-700"}`} data-testid={`${testid}-${v}`}>
          ₹{v.toLocaleString("en-IN")}
        </button>
      ))}
    </div>
  );
}

export function Segment({ items, value, onChange, testid = "m-seg" }) {
  return (
    <div className="grid gap-1 rounded-xl bg-slate-100 p-1" style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }} data-testid={testid}>
      {items.map(([v, l]) => <button type="button" key={v} onClick={() => onChange(v)} className={`h-11 rounded-lg text-sm font-bold ${value === v ? "bg-[#F26B1D] text-white shadow" : "text-slate-600"}`} data-testid={`${testid}-${v}`}>{l}</button>)}
    </div>
  );
}

export function Radio({ on }) {
  return <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 ${on ? "border-[#F26B1D]" : "border-slate-300"}`}>{on && <span className="h-2.5 w-2.5 rounded-full bg-[#F26B1D]" />}</span>;
}

export function FeeBox({ fee, total, feeLabel = "Processing Fee" }) {
  return (
    <div className="mt-5 space-y-2 rounded-2xl bg-slate-100 p-4 text-sm" data-testid="m-fee-box">
      <div className="flex justify-between text-slate-600"><span>{feeLabel}</span><span className="font-semibold" data-testid="m-fee">{fee === null ? "As per slab" : rs(fee)}</span></div>
      <div className="flex justify-between text-base font-bold"><span>Total Amount</span><span data-testid="m-total">{rs(total)}</span></div>
    </div>
  );
}

export function CTA({ children, loading, ...p }) {
  return <button {...p} disabled={loading || p.disabled} className="mt-6 h-14 w-full rounded-xl bg-[#0F2C47] text-base font-bold text-white active:scale-[0.99] disabled:opacity-50">{loading ? "Please wait…" : children}</button>;
}

export function MpinSheet({ title, amount, rows = [], busy, onPay, onCancel }) {
  const [mpin, setMpin] = useState("");
  useEffect(() => { if (mpin.length === 4) onPay(mpin); }, [mpin]); // eslint-disable-line
  useEffect(() => { if (!busy) setMpin(""); }, [busy]);
  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end bg-black/40" data-testid="m-mpin-sheet">
      <div className="rounded-t-[28px] bg-white px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-5">
        <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-black/10" />
        <div className="text-center text-sm text-slate-500">{title}</div>
        <div className="text-center font-display text-3xl font-bold" data-testid="m-sheet-amount">{rs(amount)}</div>
        {rows.length > 0 && <div className="mt-3 space-y-1 rounded-2xl bg-slate-100 p-3 text-sm">{rows.map(([k, v]) => <div key={k} className="flex justify-between"><span className="text-slate-500">{k}</span><span className="font-semibold">{v}</span></div>)}</div>}
        <div className="my-4 text-center text-sm font-semibold">{busy ? "Processing…" : "Enter MPIN to confirm"}</div>
        <PinPad value={mpin} onChange={busy ? () => {} : setMpin} testid="m-mpin" />
        <button onClick={onCancel} disabled={busy} className="mt-3 w-full py-2 text-sm font-semibold text-slate-500" data-testid="m-mpin-cancel">Cancel</button>
      </div>
    </div>
  );
}

export function usePay(url) {
  const { refreshBalance } = useAuth();
  const [sheet, setSheet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [slip, setSlip] = useState(null);
  const pay = async (mpin) => {
    setBusy(true);
    try { const { data } = await api.post(url, { ...sheet.body, mpin }); setSheet(null); setSlip(data); refreshBalance(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const ui = (
    <>
      {sheet && <MpinSheet title={sheet.title} amount={sheet.amount} rows={sheet.rows} busy={busy} onPay={pay} onCancel={() => setSheet(null)} />}
      {slip && <Slip txn={slip} onClose={() => setSlip(null)} />}
    </>
  );
  return { open: setSheet, ui, done: !!slip };
}
